/**
 * Das Formular der globalen Regelkonfiguration — §52, §67 Schritte 2 und 3.
 *
 * **Gespeichert wird immer die ganze Konfiguration.** `PUT /api/v1/admin/rule-config` ersetzt
 * vollständig: weggelassene Felder fallen auf ihren Default zurück (`.prefault()` im Schema).
 * Dieses Formular hält deshalb den geladenen Stand als Ganzes und ersetzt darin einzelne Blätter
 * (`admin/paths.ts`). Ein Formular, das nur Änderungen sammelt und schickt, würde bei jeder
 * Speicherung alles Übrige zurückdrehen — und das fiele erst an den Spielergebnissen auf.
 *
 * **Die Felder stehen nicht hier, sondern in `admin/fields.ts`.** Welche Werte es gibt, sagt das
 * Schema; dass jedes davon ein Eingabefeld hat, prüft ein Test. Diese Datei weiß nur, wie man
 * eine Zahl, einen Wahrheitswert und die beiden Sonderformen bedient.
 *
 * **Zahlen werden als Text gehalten, solange getippt wird.** Ein kontrolliertes Zahlenfeld, dessen
 * Wert direkt aus der Konfiguration kommt, springt beim Leeren auf den alten Wert zurück — und die
 * nächste Ziffer landet davor (`1` → `13` statt `3`). Der Rohtext liegt deshalb neben der
 * Konfiguration und wird erst übernommen, wenn er eine Zahl ist. Ein leeres Feld bei einem
 * `nullableInt` ist dagegen ein Wert: **unbegrenzt** (§16).
 *
 * **Eine abgewiesene Konfiguration zeigt die Begründung des Dienstes.** Die Querprüfungen des
 * Schemas (Startreihen gegen Brettgröße, Figurenzahl gegen Startfelder) antworten mit 400 und
 * einem Satz ohne zod-Pfad. Nur die Übersetzung des Codes zu zeigen — „Die Anfrage hatte nicht die
 * erwartete Form" — wäre wahr und für einen Balancing-Durchlauf nutzlos.
 */

import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import type { RuleConfig } from '@flaghex/engine';

import { useRequest } from '../api/context.js';
import { ruleConfig as loadRuleConfig, saveRuleConfig } from '../api/endpoints.js';
import type { ApiFailure } from '../api/transport.js';
import {
  PASSAGE_OPTIONS,
  RULE_SECTIONS,
  type FieldKind,
  type RuleField,
} from './fields.js';
import { valueAt, withValueAt } from './paths.js';

export interface RuleConfigFormProps {
  readonly adminKey: string;
  /** Der Schlüssel gilt nicht (mehr) — die Schale fragt dann erneut nach ihm. */
  readonly onUnauthenticated: () => void;
}

/** Der Rohtext eines Zahlenfeldes, solange daran getippt wird. */
type Texts = Readonly<Record<string, string>>;

/** Wie ein Wert in einem Eingabefeld erscheint. */
function asText(value: unknown): string {
  return value === null || value === undefined ? '' : String(value);
}

function sameConfig(a: RuleConfig, b: RuleConfig): boolean {
  // Die Reihenfolge der Schlüssel bleibt stabil, weil `withValueAt` bestehende Objekte spreizt.
  return JSON.stringify(a) === JSON.stringify(b);
}

export function RuleConfigForm({ adminKey, onUnauthenticated }: RuleConfigFormProps) {
  const { t } = useTranslation(['admin', 'pieces', 'errors', 'common']);
  const request = useRequest();

  const [loaded, setLoaded] = useState<RuleConfig | undefined>(undefined);
  const [draft, setDraft] = useState<RuleConfig | undefined>(undefined);
  const [texts, setTexts] = useState<Texts>({});
  const [saved, setSaved] = useState(false);
  const [failure, setFailure] = useState<{ phase: 'load' | 'save'; cause: ApiFailure } | undefined>(
    undefined,
  );

  useEffect(() => {
    let current = true;
    void loadRuleConfig(request, adminKey).then((result) => {
      if (!current) return;
      if (result.kind === 'ok') {
        setLoaded(result.value);
        setDraft(result.value);
        return;
      }
      if (result.code === 'UNAUTHENTICATED') {
        onUnauthenticated();
        return;
      }
      setFailure({ phase: 'load', cause: result });
    });
    return () => {
      current = false;
    };
  }, [request, adminKey, onUnauthenticated]);

  const replace = useCallback((path: string, value: unknown): void => {
    setSaved(false);
    setDraft((current) => (current === undefined ? current : withValueAt(current, path, value)));
  }, []);

  const typeNumber = useCallback(
    (field: { path: string; kind: FieldKind }, raw: string): void => {
      setTexts((current) => ({ ...current, [field.path]: raw }));
      const trimmed = raw.trim();
      if (trimmed === '') {
        // Leer heißt bei einer offenen Obergrenze **unbegrenzt**; bei einer Pflichtzahl heißt es
        // nur „noch nichts getippt", und der bisherige Wert bleibt stehen.
        if (field.kind === 'nullableInt') replace(field.path, null);
        return;
      }
      const parsed = Number(trimmed);
      if (Number.isInteger(parsed)) replace(field.path, parsed);
    },
    [replace],
  );

  const save = useCallback((): void => {
    if (draft === undefined) return;
    setFailure(undefined);
    void saveRuleConfig(request, adminKey, draft).then((result) => {
      if (result.kind === 'ok') {
        // Der Dienst antwortet mit dem gespeicherten Stand — er ist die Wahrheit, nicht der
        // Entwurf: Defaults und Normalisierungen sind darin schon angewandt.
        setLoaded(result.value);
        setDraft(result.value);
        setTexts({});
        setSaved(true);
        return;
      }
      if (result.code === 'UNAUTHENTICATED') {
        onUnauthenticated();
        return;
      }
      setFailure({ phase: 'save', cause: result });
    });
  }, [draft, request, adminKey, onUnauthenticated]);

  const discard = useCallback((): void => {
    setDraft(loaded);
    setTexts({});
    setSaved(false);
    setFailure(undefined);
  }, [loaded]);

  if (failure?.phase === 'load') {
    return (
      <section>
        <h2>{t('admin:rules.heading')}</h2>
        <p role="alert">{t(`errors:${failure.cause.code}`)}</p>
      </section>
    );
  }

  if (draft === undefined || loaded === undefined) {
    return (
      <section>
        <h2>{t('admin:rules.heading')}</h2>
        <p role="status">{t('common:loading')}</p>
      </section>
    );
  }

  const changed = !sameConfig(draft, loaded);

  /** Ein einzelnes Feld, nach seiner Art. */
  const input = (field: RuleField): ReactNode => {
    const value = valueAt(draft, field.path);
    const label = t(`admin:labels.${field.label}`);

    switch (field.kind) {
      case 'text':
        return (
          <label>
            {label}{' '}
            <input
              type="text"
              data-path={field.path}
              value={asText(value)}
              onChange={(event) => replace(field.path, event.target.value)}
            />
          </label>
        );

      case 'bool':
        return (
          <label>
            <input
              type="checkbox"
              data-path={field.path}
              checked={value === true}
              onChange={(event) => replace(field.path, event.target.checked)}
            />{' '}
            {label}
          </label>
        );

      case 'int':
      case 'nullableInt':
        return (
          <label>
            {label}{' '}
            <input
              type="number"
              min={field.min ?? 1}
              step={1}
              data-path={field.path}
              value={texts[field.path] ?? asText(value)}
              onChange={(event) => typeNumber(field, event.target.value)}
            />
            {field.kind === 'nullableInt' ? ` (${t('admin:rules.unlimitedHint')})` : null}
          </label>
        );

      case 'passage':
        return (
          <label>
            {label}{' '}
            <select
              data-path={field.path}
              value={asText(value)}
              onChange={(event) => replace(field.path, event.target.value)}
            >
              {PASSAGE_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {t(`admin:passage.${option}`)}
                </option>
              ))}
            </select>
          </label>
        );

      case 'capture':
        return captureInput(field, value);
    }
  };

  /**
   * Die Schlagreichweite — Marker oder Zahlenpaar.
   *
   * Beim Abschalten des Markers werden **genau die Bewegungswerte** übernommen. Das ist
   * bedeutungsgleich: `'sameAsMovement'` *hieß* „so weit wie die Bewegung". Eine erfundene Zahl
   * wäre dagegen eine stillschweigend erfundene Regel (Regel 5) und verfälschte das Balancing.
   */
  function captureInput(field: RuleField, value: unknown): ReactNode {
    const marker = value === 'sameAsMovement';
    const mirrored = field.mirrors === undefined ? undefined : valueAt(draft, field.mirrors);
    const range = typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : undefined;

    const toggle = (checked: boolean): void => {
      if (checked) {
        replace(field.path, 'sameAsMovement');
        return;
      }
      const movement = (mirrored ?? {}) as Record<string, unknown>;
      replace(field.path, { min: movement.min, max: movement.max });
    };

    return (
      <>
        <label>
          <input
            type="checkbox"
            data-path={field.path}
            checked={marker}
            onChange={(event) => toggle(event.target.checked)}
          />{' '}
          {t('admin:labels.capture')}: {t('admin:rules.sameAsMovement')}
        </label>
        {marker || range === undefined ? null : (
          // Eigene Listeneinträge, nicht nur eigene Beschriftungen: im Browser stand sonst
          // „so weit wie die Bewegungmindestens 1" in einer Zeile — grün in jedem Test.
          <ul>
            <li>
              <label>
                {t('admin:rules.min')}{' '}
                <input
                  type="number"
                  min={1}
                  step={1}
                  data-path={`${field.path}.min`}
                  value={texts[`${field.path}.min`] ?? asText(range.min)}
                  onChange={(event) =>
                    typeNumber({ path: `${field.path}.min`, kind: 'int' }, event.target.value)
                  }
                />
              </label>
            </li>
            <li>
              <label>
                {t('admin:rules.max')}{' '}
                <input
                  type="number"
                  min={1}
                  step={1}
                  data-path={`${field.path}.max`}
                  value={texts[`${field.path}.max`] ?? asText(range.max)}
                  onChange={(event) =>
                    typeNumber(
                      { path: `${field.path}.max`, kind: 'nullableInt' },
                      event.target.value,
                    )
                  }
                />
                {` (${t('admin:rules.unlimitedHint')})`}
              </label>
            </li>
          </ul>
        )}
      </>
    );
  }

  return (
    <section>
      <h2>{t('admin:rules.heading')}</h2>
      <p>{t('admin:rules.intro')}</p>

      {RULE_SECTIONS.map((section) => (
        <fieldset key={section.piece ?? section.group}>
          <legend>
            {section.piece === undefined
              ? t(`admin:groups.${section.group ?? ''}`)
              : t(`pieces:${section.piece}`)}
          </legend>
          <ul>
            {section.fields.map((field) => (
              <li key={field.path}>{input(field)}</li>
            ))}
          </ul>
        </fieldset>
      ))}

      <p role="status">{changed ? t('admin:rules.changed') : t('admin:rules.unchanged')}</p>
      {saved ? <p role="status">{t('admin:rules.saved')}</p> : null}

      {failure === undefined ? null : (
        <div role="alert">
          <p>{t('admin:rules.rejected')}</p>
          <p>{t(`errors:${failure.cause.code}`)}</p>
          {failure.cause.message === '' ? null : <p>{failure.cause.message}</p>}
        </div>
      )}

      <p>
        <button type="button" onClick={save} disabled={!changed}>
          {t('admin:rules.save')}
        </button>{' '}
        <button type="button" onClick={discard} disabled={!changed}>
          {t('admin:rules.discard')}
        </button>
      </p>
    </section>
  );
}
